import {
  ComponentRef,
  FC,
  forwardRef,
  SelectHTMLAttributes,
  useMemo,
} from 'react';

import { cva } from 'class-variance-authority';
import { BsInfoCircle } from 'react-icons/bs';
import ReactSelect, {
  ActionMeta,
  ControlProps,
  OptionProps,
  SingleValue,
  components,
} from 'react-select';
import StateManagedSelect from 'react-select/dist/declarations/src/stateManager';
import { Tooltip } from 'ui/components/common';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

const subTextClasses = cva(['text-xs'], {
  variants: {
    textType: {
      default: [],
      success: ['text-primary-base'],
      danger: ['text-danger-base'],
      warning: ['text-warning-base'],
    },
  },
  defaultVariants: {
    textType: 'default',
  },
});

const selectClasses = cva(
  [
    'text-content w-full',
    '!shadow-none rounded-[6px]',
    'text-sm',
    'border-[0.5px] border-separator-primary',
    'disabled:border-fill-secondary disabled:text-disabled',
    'group-focus:border-primary-base group-focus-visible:border-primary-base',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['text-sm', '!h-9'],
        [SIZE.Medium]: ['text-sm', 'h-[42px]'],
        [SIZE.Large]: ['text-base', 'h-[52px]', 'px-2'],
      },
      isFocused: {
        true: ['!border-primary-base'],
      },
      state: {
        danger: ['!border-danger-base'],
        warning: ['!border-warning-base'],
        success: ['!border-success-base'],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
    },
  },
);

interface CONTROL_PROPS extends ControlProps<any> {
  textType?: 'default' | 'danger' | 'success' | 'warning';
}

const Control: FC<CONTROL_PROPS> = ({ children, textType, ...props }) => {
  // const selected = props.getValue();
  return (
    <components.Control
      {...props}
      className={cn(
        'flex items-center px-2 !bg-fill-secondary',
        '!border-[0.5px]',
        '!shadow-none !rounded-[6px] !min-h-[36px] h-full',
        textType === 'danger'
          ? '!border-danger-base !text-danger-base'
          : '!border-separator-secondary',
      )}
    >
      {children}
    </components.Control>
  );
};

const Option = (props: OptionProps<any>) => {
  const { data, isFocused, isSelected } = props;
  return (
    <components.Option
      {...props}
      className={cn(
        '!flex gap-[14px] !items-center',
        'px-4 py-[10px] hover:bg-fill-primary',
        (isSelected || isFocused) &&
          '!bg-fill-primary !text-secondary-dark font-semibold',
      )}
    >
      <div>
        <div className="text-sm leading-5 tracking-wider text-secondary">{`${data?.label}`}</div>
        <div className="text-content text-xs leading-4 tracking-wider">
          {data?.description}
        </div>
      </div>
    </components.Option>
  );
};

interface PROPS
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange'> {
  label?: string;
  size?: SIZE;
  labelSubText?: string;
  information?: string;
  subText?: string;
  options: OPTIONS_PROPS[];
  selectClassName?: string;
  textType?: 'default' | 'danger' | 'success' | 'warning';
  onChange: (newValue: SingleValue<any>, actionMeta: ActionMeta<any>) => void;
  placeholder?: string;
  isSearchable?: boolean;
}

const Select = forwardRef<ComponentRef<typeof ReactSelect>, PROPS>(
  (
    {
      label,
      labelSubText = '(Optional)',
      information,
      subText,
      options,
      selectClassName,
      textType,
      className,
      name,
      required,
      disabled,
      placeholder = '',
      onChange,
      size,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const defaultVal = useMemo(
      () => options.find((opt) => opt.value === defaultValue),
      [defaultValue, options],
    );

    return (
      <div className={cn('flex flex-col gap-y-2', className)}>
        {label && (
          <div className="flex items-center justify-between">
            {label && (
              <label htmlFor={name}>
                <span className="mr-1 text-sm font-semibold">{label}</span>
                {required ? (
                  <span className="text-danger-base">*</span>
                ) : (
                  <span className="text-xs text-content">{labelSubText}</span>
                )}
              </label>
            )}
            {!!information && (
              <Tooltip placement="right" title={information} theme="dark">
                <div className="input-info">
                  <BsInfoCircle />
                </div>
              </Tooltip>
            )}
          </div>
        )}
        <div className="relative w-full">
          <ReactSelect
            ref={ref}
            placeholder={placeholder}
            defaultValue={defaultVal}
            value={defaultVal}
            className={cn(selectClasses({ size }), selectClassName)}
            components={{
              Control: (props) => <Control {...props} textType={textType} />,
              Option,
              IndicatorSeparator: null,
            }}
            isDisabled={disabled}
            isSearchable={false}
            name="color"
            onChange={onChange}
            styles={{
              valueContainer: (base) => ({ ...base, padding: '0 2px' }),
            }}
            options={options}
            isOptionDisabled={(option: any) => !!option.disabled}
            {...(props as StateManagedSelect)}
          />
        </div>
        {subText && (
          <div className={cn(subTextClasses({ textType }))}>{subText}</div>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
